import {
  computed,
  ref
} from "vue";
import {
  defineStore
} from "pinia";

import {
  getGameSettings,
  updateGameSettings
} from "@/api/settingsApi.js";
import {
  DEFAULT_LANGUAGE,
  getHtmlLanguage,
  getLanguageOptions,
  normalizeLanguage
} from "@/i18n/messages.js";
import {
  translate,
  translateError
} from "@/i18n/messages.js";
import {
  gameRuntimeBridge
} from "@/game/bridge/runtimeBridge.js";


function normalizeStoredLanguage(code) {
  const value = String(code || "").trim();

  if (value === "zh") {
    return "zh-CN";
  }

  return normalizeLanguage(value);
}


function toUiLanguage(code) {
  return normalizeStoredLanguage(code);
}


function toBackendLanguage(code) {
  return normalizeStoredLanguage(code);
}


export const useLanguageStore =
  defineStore(
    "language",
    () => {
      const language = ref(
        DEFAULT_LANGUAGE
      );

      const options =
        getLanguageOptions();

      const loading = ref(false);
      const saving = ref(false);
      const initialized = ref(false);
      const loadError = ref(null);
      const saveError = ref(null);
      let refreshPromise = null;


      const htmlLanguage = computed(
        () =>
          getHtmlLanguage(
            language.value
          )
      );


      function applyLanguage(
        nextLanguage
      ) {
        language.value =
          toUiLanguage(
            nextLanguage
          );

        document.documentElement.lang =
          htmlLanguage.value;

        document.documentElement.dir =
          language.value === "ar"
            ? "rtl"
            : "ltr";

        gameRuntimeBridge.setLanguage(
          language.value
        );

        return language.value;
      }


      async function refresh() {
        if (refreshPromise) {
          return refreshPromise;
        }

        loading.value = true;
        loadError.value = null;

        refreshPromise = (async () => {
          try {
            const settings =
              await getGameSettings();

            const result = applyLanguage(
              settings.languageCode
            );

            initialized.value = true;
            return result;
          } catch (error) {
            loadError.value = error;
            throw error;
          } finally {
            loading.value = false;
            refreshPromise = null;
          }
        })();

        return refreshPromise;
      }


      async function initialize() {
        if (initialized.value) {
          return language.value;
        }

        return refresh();
      }


      async function setLanguage(
        nextLanguage
      ) {
        if (saving.value) {
          return language.value;
        }

        saving.value = true;
        saveError.value = null;

        const selectedLanguage =
          applyLanguage(nextLanguage);

        try {
          const settings =
            await updateGameSettings({
              languageCode:
                toBackendLanguage(
                  nextLanguage
                )
            });

          const result = applyLanguage(
            settings.languageCode
          );

          initialized.value = true;
          return result;
        } catch (error) {
          saveError.value = error;
          initialized.value = true;

          applyLanguage(selectedLanguage);
          throw error;
        } finally {
          saving.value = false;
        }
      }


      function t(key, params) {
        return translate(
          language.value,
          key,
          params
        );
      }

      function errorText(error, fallbackKey = "starMapImageError") {
        return translateError(
          language.value,
          error,
          fallbackKey
        );
      }


      document.documentElement.lang =
        htmlLanguage.value;

      document.documentElement.dir =
        language.value === "ar"
          ? "rtl"
          : "ltr";


      return {
        language,
        options,
        htmlLanguage,
        loading,
        saving,
        initialized,
        loadError,
        saveError,
        initialize,
        refresh,
        setLanguage,
        t,
        errorText
      };
    }
  );
